<template>
  <div class="logo-upload" :class="{ 'drag-state': isDragging }">
    <label v-if="!existingImage || edit" :for="id">
      <input
        :id="id"
        type="file"
        :name="id"
        :accept="acceptedFileTypes"
        @change="uploadFile"
        @dragover="onDragOverEnter"
        @dragenter="onDragOverEnter"
        @dragleave="onDragLeaveDrop"
        @drop="onDragLeaveDrop"
      />
      <div v-if="!isDragging" class="d-flex empty-state align-items-center">
        <div
          class="d-flex align-items-center justify-content-center icon-wrapper"
        >
          <i-icon symbol="small-download-button-2" :size="20"></i-icon>
        </div>
        <div class="d-flex flex-column empty-state__text">
          <span class="empty-state__heading">
            Choose file or drag and drop
          </span>
          <small class="empty-state__subheading"> SVG format accepted </small>
        </div>
      </div>
      <div
        v-if="isDragging"
        class="drop-message d-flex align-items-center justify-content-center"
      >
        <h5>Drag and drop file here</h5>
      </div>
    </label>
    <i-spinner v-if="loading" class="i-mt-8"></i-spinner>
    <div v-if="existingImage && !edit && !loading" class="existing-logo">
      <img
        class="existing-logo__image"
        :src="existingImage"
        alt="Existing Logo"
      />
      <button class="existing-logo__close-button" @click="modify">
        <i-icon
          symbol="small-delete"
          class="i-text-neutral-80"
          :size="20"
        ></i-icon>
      </button>
    </div>
  </div>
</template>

<script>
import { iIcon, iSpinner } from '@inscribe/signal';

export default {
  components: { iIcon, iSpinner },
  props: {
    id: {
      type: String,
      default: 'logoUpload',
    },
    acceptedFileTypes: {
      type: Array,
      default: () => ['image/jpg', 'image/jpeg', 'image/png', 'image/svg+xml'],
    },
    changeAction: {
      type: Function,
      default: undefined,
    },
    existingImage: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      edit: false,
      uploadedFile: [],
      isDragging: false,
      loading: false,
    };
  },
  watch: {
    existingImage: {
      handler: function (val) {
        this.loading = false;
      },
    },
  },
  methods: {
    uploadFile(event) {
      this.uploadedFile = event.target.files || event.dataTransfer.files;
      if (this.uploadedFile.length) {
        const originalFile = this.uploadedFile[0];
        const newName = originalFile.name.replace(/ /g, '_');
        const fileToUpload = new File([originalFile], newName, {
          type: originalFile.type,
        });
        this.changeAction(fileToUpload);
        this.loading = true;
        this.edit = false;
      }
    },
    modify() {
      this.edit = true;
    },
    onDragOverEnter() {
      this.isDragging = true;
    },
    onDragLeaveDrop() {
      this.isDragging = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.logo-upload {
  border: 1px solid $color-almost-black-60;
  border-radius: 12px;
  position: relative;
  height: 90px;
  &.drag-state {
    background: #f5f5f5;
    .drop-message {
      width: 100%;
      h5 {
        font-size: $font-size-default;
        font-weight: 600;
        margin: 0;
      }
    }
  }

  label {
    height: 100%;
    color: $color-almost-black;
    margin: 0;
    cursor: pointer;
    padding: 16px 16px 16px 24px;

    input[type='file'] {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 100%;
      cursor: pointer;
      opacity: 0;
      &:focus {
        outline: none;
      }
    }

    &:after {
      content: '';
    }
    .empty-state {
      &__heading {
        font-weight: bold;
        line-height: 20px;
        margin-bottom: 2px;
      }
      &__subheading {
        font-size: $font-size-default;
        color: $color-almost-black-80;
      }
    }
  }

  &:hover,
  &:active {
    border-color: $color-almost-black-80;
  }

  .icon-wrapper {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    background: $color-navy-blue-10;
    border-radius: 50%;
    margin-right: 12px;
    svg {
      margin-left: 0 !important;
      color: #485d74;
    }
  }

  .existing-logo {
    display: flex;
    padding: 16px 24px;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    animation: fadeIn 3s;
    &__image {
      height: 48px;
    }

    &__close-button {
      outline: 0;
      border: none;
      background: none;
      svg {
        color: rgba($color-navy-blue-60, 0.5);
      }
      &:hover,
      &:active,
      &:focus {
        svg {
          color: $color-almost-black-80;
        }
      }
    }
  }
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}
</style>
